/**
 * MyScheduleView — the engineer role's landing page.
 *
 * Fetches GET /api/engineer/schedule (scoped server-side to the signed-in
 * engineer's own manpower assignments — never anyone else's, never financials)
 * and shows the assignments as a TABLE (upcoming / recent: date, project,
 * GLOBAL #, location) or a month CALENDAR - the engineer's pick, remembered
 * in this browser (10/9/26; no customer column, engineers do not need it).
 *
 * If the account isn't linked to an engineer record yet ({linked:false}), we
 * show a friendly "ask an admin to link your account" card instead.
 *
 * A calendar save by the office (r50 live events, kind 'manpower') re-reads
 * it, quietly: a re-read that fails keeps the schedule on screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { localDay } from '../../utils/localDay';

const CARD = {
  background: '#fff',
  border: '1px solid #ECECEC',
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};
const TH = {
  textAlign: 'left',
  padding: '10px 14px',
  fontSize: 10,
  fontWeight: 800,
  color: '#888',
  letterSpacing: 1,
  textTransform: 'uppercase',
  borderBottom: '2px solid #1B5E20',
};
const TD = { padding: '12px 14px', fontSize: 12.5, color: '#0D0D0D', borderBottom: '1px solid #EEE' };

// "2026-07-06" -> "Mon, Jul 6" (locale-free, no timezone shift — parse parts).
const fmtDate = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = String(iso)
    .slice(0, 10)
    .split('-')
    .map((n) => parseInt(n, 10));
  if (!y) return '-';
  // "Mon, Oct 5"
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

function AssignmentTable({ rows }) {
  return (
    <div style={CARD}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={TH}>Date</th>
            <th style={TH}>Project</th>
            <th style={TH}>GLOBAL #</th>
            <th style={TH}>Location</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.date}-${r.projectLabel}-${i}`}>
              <td style={{ ...TD, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
              <td style={{ ...TD, fontWeight: 700 }}>{r.projectLabel || '-'}</td>
              <td style={{ ...TD, color: '#555', fontVariantNumeric: 'tabular-nums' }}>{r.globalProjectNumber || '-'}</td>
              <td style={{ ...TD, color: '#666' }}>{r.location || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MODE_KEY = 'globalops_my_schedule_view';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad2 = (n) => String(n).padStart(2, '0');
const todayIso = () => localDay();
// one light color per project label, the same every time
const TINTS = ['#E8F5E9', '#E3F2FD', '#FFF3E0', '#F3E5F5', '#E0F7FA', '#FFFDE7', '#FCE4EC', '#EDE7F6'];
const tintOf = (label) => TINTS[[...String(label || '')].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 9973, 0) % TINTS.length];

/** A month grid (Monday first) of the engineer's days: project, GLOBAL #, location. */
function ScheduleCalendar({ rows }) {
  const byDay = new Map();
  for (const r of rows) byDay.set(r.date, [...(byDay.get(r.date) || []), r]);
  const today = todayIso();
  const [month, setMonth] = useState(() => today.slice(0, 7)); // 'YYYY-MM'
  const [y, m] = month.split('-').map(Number);
  const shift = (n) => {
    const d = new Date(y, m - 1 + n, 1);
    setMonth(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  };
  const lead = (new Date(y, m - 1, 1).getDay() + 6) % 7; // blank cells before the 1st
  const days = new Date(y, m, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => `${month}-${pad2(i + 1)}`)];
  while (cells.length % 7) cells.push(null);
  const navBtn = {
    border: '1px solid #D8DED4',
    background: '#fff',
    borderRadius: 6,
    padding: '4px 10px',
    cursor: 'pointer',
    fontWeight: 700,
    fontFamily: 'inherit',
  };
  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '2px solid #1B5E20' }}>
        <button type="button" aria-label="Previous month" onClick={() => shift(-1)} style={navBtn}>
          ‹
        </button>
        <div style={{ fontSize: 13, fontWeight: 800, minWidth: 140, textAlign: 'center' }}>
          {MONTHS[m - 1]} {y}
        </div>
        <button type="button" aria-label="Next month" onClick={() => shift(1)} style={navBtn}>
          ›
        </button>
        <button type="button" onClick={() => setMonth(today.slice(0, 7))} style={{ ...navBtn, marginLeft: 6, fontSize: 11 }}>
          Today
        </button>
      </div>
      <div role="grid" aria-label={`${MONTHS[m - 1]} ${y}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div
            key={d}
            style={{
              padding: '6px 8px',
              fontSize: 10,
              fontWeight: 800,
              color: '#888',
              letterSpacing: 1,
              textTransform: 'uppercase',
              borderBottom: '1px solid #EEE',
            }}
          >
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          const list = (day && byDay.get(day)) || [];
          const weekend = i % 7 >= 5;
          return (
            <div
              key={day || `x${i}`}
              role="gridcell"
              aria-label={day || undefined}
              style={{
                minHeight: 84,
                padding: 6,
                borderRight: i % 7 === 6 ? 'none' : '1px solid #F1F1F1',
                borderBottom: '1px solid #F1F1F1',
                background: !day ? '#FAFAFA' : weekend ? '#FBFBF9' : '#fff',
                outline: day === today ? '2px solid #1B5E20' : 'none',
                outlineOffset: -2,
              }}
            >
              {day && (
                <div style={{ fontSize: 11, fontWeight: 700, color: day === today ? '#1B5E20' : '#999', marginBottom: 4 }}>
                  {Number(day.slice(8))}
                </div>
              )}
              {list.map((r, k) => (
                <div
                  key={k}
                  title={[r.projectLabel, r.globalProjectNumber, r.location].filter(Boolean).join(' · ')}
                  style={{ background: tintOf(r.projectLabel), borderRadius: 5, padding: '3px 5px', marginBottom: 3 }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#0D0D0D',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.projectLabel}
                  </div>
                  {r.globalProjectNumber && (
                    <div style={{ fontSize: 10, color: '#555', fontVariantNumeric: 'tabular-nums' }}>{r.globalProjectNumber}</div>
                  )}
                  {r.location && <div style={{ fontSize: 9.5, color: '#777' }}>{r.location}</div>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyScheduleView() {
  const [mode, setMode] = useState(() => {
    try {
      return window.localStorage.getItem(MODE_KEY) === 'calendar' ? 'calendar' : 'table';
    } catch {
      return 'table';
    }
  });
  const pickMode = (m) => {
    setMode(m);
    try {
      window.localStorage.setItem(MODE_KEY, m);
    } catch {
      /* private window */
    }
  };
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const alive = useRef(true);
  const [reads] = useState(latestReads); // the first read and live re-reads can overlap

  // `quiet` (a live re-read): a failure keeps what is shown
  const read = useCallback(
    (quiet) => {
      const t = reads.start();
      const take = (next) => {
        if (alive.current && reads.show(t)) setState(next);
      };
      return fetch('/api/engineer/schedule')
        .then((r) => {
          if (quiet === true && !r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data) => take({ loading: false, error: null, data }))
        .catch((err) => {
          if (quiet !== true) take({ loading: false, error: err.message || 'Failed to load', data: null });
        });
    },
    [reads]
  );

  useEffect(() => {
    alive.current = true;
    read();
    return () => {
      alive.current = false;
    };
  }, [read]);
  const reReadQuietly = useCallback(() => read(true), [read]);
  useLiveRefresh('manpower', reReadQuietly);

  const { loading, error, data } = state;

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888', fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>
        Loading your schedule…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#B71C1C', fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>
        Couldn't load your schedule. Please try again.
      </div>
    );
  }

  // Not linked yet — friendly guidance card.
  if (data && data.linked === false) {
    return (
      <div style={{ fontFamily: "'Montserrat', sans-serif", display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
        <div
          style={{
            maxWidth: 460,
            textAlign: 'center',
            background: '#FFF8E1',
            border: '1px solid #FFE082',
            borderRadius: 12,
            padding: '28px 26px',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, color: '#8D6E00', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>
            Account not linked
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#5D4A00', marginBottom: 10 }}>No schedule to show yet</div>
          <p style={{ fontSize: 12.5, color: '#6D5A1A', lineHeight: 1.6, margin: 0 }}>
            {data.message || 'Your account is not linked to an engineer record yet. Ask an admin to link you in Team & Access.'}
          </p>
        </div>
      </div>
    );
  }

  const upcoming = data?.upcoming || [];
  const recent = data?.recent || [];
  const engName = data?.engineer?.name;

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#1B5E20', letterSpacing: 2, textTransform: 'uppercase' }}>
          Engineer · My Schedule
        </div>
        <p style={{ fontSize: 12.5, color: '#555', margin: '4px 0 0' }}>
          {engName ? (
            <>
              Assignments for <b>{engName}</b>.{' '}
            </>
          ) : null}
          Your upcoming project assignments from the manpower calendar.
        </p>
      </div>

      <div role="group" aria-label="How to show the schedule" style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          ['table', 'Table'],
          ['calendar', 'Calendar'],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            aria-pressed={mode === k}
            onClick={() => pickMode(k)}
            style={{
              border: '1px solid',
              borderColor: mode === k ? '#1B5E20' : '#D8DED4',
              background: mode === k ? '#1B5E20' : '#fff',
              color: mode === k ? '#fff' : '#333',
              borderRadius: 20,
              padding: '5px 16px',
              fontSize: 11.5,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'calendar' ? (
        <ScheduleCalendar rows={[...recent, ...upcoming]} />
      ) : (
        <>
          <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 800, color: '#0D0D0D' }}>Upcoming</div>
          {upcoming.length > 0 ? (
            <AssignmentTable rows={upcoming} />
          ) : (
            <div style={{ ...CARD, padding: 28, textAlign: 'center', color: '#999', fontSize: 12.5 }}>
              No upcoming assignments scheduled.
            </div>
          )}

          {recent.length > 0 && (
            <>
              <div style={{ margin: '22px 0 10px', fontSize: 13, fontWeight: 800, color: '#0D0D0D' }}>Recent</div>
              <AssignmentTable rows={recent} />
            </>
          )}
        </>
      )}
    </div>
  );
}
